import { safeStorageGet, safeStorageSet } from '../storage/safe-storage.js';

const STORAGE_KEY = 'adminsr_sidebar_folded';

/**
 * Sidebar fold/open state manager. Pure logic — no DOM dependency.
 *
 * Desktop: fold()/unfold()/toggleFold() + isFolded(). Persisted to localStorage.
 * Mobile: open()/close() + isOpen(). NEVER persisted.
 *
 * toggleFold() has dual behavior by viewport:
 *   - Desktop: toggles persisted folded state (fold/unfold).
 *   - Mobile: toggles transient overlay (open/close).
 *
 * @param {{ isMobile: () => boolean }} opts
 */
export function createFoldState({ isMobile }) {
  let folded = safeStorageGet(STORAGE_KEY, false);
  let mobileOpen = false;

  /** @type {Array<() => void>} */
  let subscribers = [];

  function notify() {
    for (const fn of subscribers) fn();
  }

  return {
    // Desktop
    isFolded() {
      return folded;
    },

    fold() {
      if (folded) return;
      folded = true;
      safeStorageSet(STORAGE_KEY, true);
      notify();
    },

    unfold() {
      if (!folded) return;
      folded = false;
      safeStorageSet(STORAGE_KEY, false);
      notify();
    },

    toggleFold() {
      if (isMobile()) {
        mobileOpen ? this.close() : this.open();
      } else {
        folded ? this.unfold() : this.fold();
      }
    },

    // Mobile
    isOpen() {
      return mobileOpen;
    },

    open() {
      if (mobileOpen) return;
      mobileOpen = true;
      notify();
    },

    close() {
      if (!mobileOpen) return;
      mobileOpen = false;
      notify();
    },

    // Shared
    /** @param {() => void} fn */
    subscribe(fn) {
      subscribers.push(fn);
      return () => {
        subscribers = subscribers.filter((s) => s !== fn);
      };
    },

    destroy() {
      subscribers = [];
    },
  };
}
